import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useConsole } from "../lib/console";
import { attackLabel, attackVar, fmtDateTime, num, pct, sevTone } from "../lib/format";
import { Badge, Button, Callout, CopyButton, CountryTag, Drawer, KV, LinkButton, SevBadge, cx, useToast } from "./ui";
import { Icon } from "./Icon";
import { BarList } from "./charts";

const fmtBytes = (b) => (b == null ? "—" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b > 1024 ? `${(b / 1024).toFixed(1)} KB` : `${b} B`);

/** Full record of a single live detection. */
export default function DetectionDrawer({ threat, onClose }) {
  const c = useConsole();
  const toast = useToast();
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => setFeedback(null), [threat]);

  if (!threat) return null;
  const t = threat;
  const ipBlocked = c.blockedIPs.has(t.source_ip);
  const user = t.username;
  const userBlocked = user && c.blockedUsers.has(user.toLowerCase());
  const loginAttack = /brute_force/.test(t.attack_type || "");

  const sendFeedback = async (label) => {
    setBusy(true);
    try {
      await api.post("/api/ml/feedback", { threat_id: t.db_id, true_label: label });
      setFeedback(label);
      toast("Feedback recorded", "ok", { detail: "The classifier uses it in its next online update." });
    } catch (e) {
      toast("Couldn't record feedback", "critical", { detail: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const alternatives = (t.alternatives || [])
    .filter((a) => a && a.attack_type)
    .map((a) => ({ key: a.attack_type, label: attackLabel(a.attack_type), value: Math.round((a.probability || 0) * 1000) / 10, color: attackVar(a.attack_type) }));

  return (
    <Drawer
      open
      onClose={onClose}
      width={600}
      title={attackLabel(t.attack_type)}
      subtitle={`${fmtDateTime(t.timestamp)} GST · ${t.direction === "outbound" ? "Outbound" : "Inbound"} ${String(t.protocol || "").toUpperCase()}`}
      icon={
        <span className={cx("sev-glyph", `tone-${sevTone(t.severity)}`)}>
          <Icon name={t.severity === "CRITICAL" ? "shieldAlert" : "radar"} size={17} />
        </span>
      }
      footer={
        <>
          {t.db_id && (
            <LinkButton href={`/api/threats/${t.db_id}/report`} target="_blank" rel="noreferrer" icon="download" variant="ghost">
              PDF report
            </LinkButton>
          )}
          <Button icon="crosshair" onClick={() => c.hunt({ source_ip: t.source_ip })}>
            Hunt this source
          </Button>
          <Button variant="primary" icon="user" onClick={() => c.openProfile(t.source_ip)}>
            Source profile
          </Button>
        </>
      }
    >
      <div className="row row-wrap" style={{ marginBottom: 18 }}>
        <SevBadge sev={t.severity} size="lg" />
        {t.contained || ipBlocked ? (
          <Badge tone="ok" size="lg" dot>
            Source blocked
          </Badge>
        ) : (
          <Badge tone="neutral" size="lg">
            Source not blocked
          </Badge>
        )}
        {t.is_anomaly && (
          <Badge tone="brand" size="lg">
            Behavioural anomaly
          </Badge>
        )}
        {t.is_vpn && (
          <Badge tone="warn" size="lg">
            VPN / proxy
          </Badge>
        )}
      </div>

      {t.direction === "outbound" && (
        <Callout tone="medium" icon="arrowUpRight" className="drawer-section" title="Outbound connection">
          An internal host ({t.destination_ip}) talked to this external address. The detection is attributed to the external party.
        </Callout>
      )}

      <div className="drawer-section">
        <h3>Who and where</h3>
        <KV
          items={[
            [
              "Source",
              <span className="row" style={{ gap: 4 }}>
                <span className="mono">
                  {t.source_ip}
                  {t.source_port ? `:${t.source_port}` : ""}
                </span>
                <CopyButton value={t.source_ip} label="Copy source IP" />
              </span>,
            ],
            ["Target", `${t.destination_ip || "—"}${t.destination_port ? `:${t.destination_port}` : ""}`, { mono: true }],
            ["Location", <CountryTag code={t.country_code} name={[t.city, t.country].filter(Boolean).join(", ") || "Unknown"} />],
            ["Network", t.isp || "—"],
            user && ["Account targeted", `@${user}`, { color: "var(--sev-high)" }],
            ["Traffic", `${fmtBytes(t.bytes_sent)} out · ${fmtBytes(t.bytes_recv)} in · ${num(t.packet_count)} pkts`],
          ]}
        />
      </div>

      <div className="drawer-section">
        <h3>Why it was flagged</h3>
        <KV
          items={[
            ["MITRE tactic", t.mitre_tactic],
            ["Technique", t.mitre_technique_id ? `${t.mitre_technique_id} · ${t.mitre_technique}` : t.mitre_technique, { wrap: true }],
            ["Classifier confidence", pct(t.confidence)],
            ["Severity score", t.severity_score != null ? `${Number(t.severity_score).toFixed(2)} / 10` : "—"],
            ["Anomaly score", t.anomaly_score != null ? Number(t.anomaly_score).toFixed(3) : "—"],
            ["Duration", t.duration_ms != null ? `${Math.round(t.duration_ms)} ms` : "—"],
          ]}
        />
        {alternatives.length > 1 && (
          <div style={{ marginTop: 18 }}>
            <div className="small muted" style={{ marginBottom: 10 }}>
              Classifier's top candidates (random-forest probability)
            </div>
            <BarList items={alternatives} share={false} valueLabel={(v) => `${v}%`} />
          </div>
        )}
      </div>

      {t.raw_log && (
        <div className="drawer-section">
          <div className="row-between" style={{ marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>Raw event</h3>
            <CopyButton value={t.raw_log} label="Copy raw event" />
          </div>
          <pre className="code-block">{t.raw_log}</pre>
        </div>
      )}

      {user && loginAttack && (
        <div className="drawer-section">
          <h3>Account response</h3>
          <div className="row-between">
            <p className="small dim" style={{ maxWidth: 360 }}>
              {userBlocked
                ? `@${user} is blocked from signing in to the protected site.`
                : `Block @${user} from signing in while the attempts are investigated.`}
            </p>
            {userBlocked ? (
              <Button variant="success" icon="unlock" onClick={() => c.allowUser(user)}>
                Restore account
              </Button>
            ) : (
              <Button variant="danger" icon="lock" onClick={() => c.blockUser(user)}>
                Block account
              </Button>
            )}
          </div>
        </div>
      )}

      {t.db_id && (
        <div className="drawer-section">
          <h3>Classification feedback</h3>
          {feedback ? (
            <Callout tone="ok" icon="checkCircle">
              Marked as {feedback === "normal" ? "a false positive" : "correctly classified"}. Thank you.
            </Callout>
          ) : (
            <div className="row-between row-wrap">
              <p className="small dim">Was “{attackLabel(t.attack_type)}” the right call? Feedback feeds the classifier's online learning.</p>
              <div className="row">
                <Button size="sm" icon="check" disabled={busy} onClick={() => sendFeedback(t.attack_type)}>
                  Correct
                </Button>
                <Button size="sm" variant="ghost" icon="x" disabled={busy} onClick={() => sendFeedback("normal")}>
                  False positive
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
